import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock } from 'lucide-react';
import { createSpecialReservation, RESERVATION_TYPES } from '../firebase/reservationService';
import LegalTermsLink from './legal/LegalTermsLink';
import DataProtectionLink from './legal/DataProtectionLink';

const SpecialRequestForm = ({ type, selectedDate, selectedPeople, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    emailConfirm: '',
    phone: '',
    phoneConfirm: '',
    comments: '',
    allergiesDetails: ''
  });

  // Estado para campos específicos de la solicitud
  const [requestDate, setRequestDate] = useState(
    selectedDate ? selectedDate.fullDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [requestTime, setRequestTime] = useState(
    type === RESERVATION_TYPES.WAITLIST ? '' : '20:00' // Por defecto vacío para lista de espera, 20:00 para grupos
  );
  const [requestGuests, setRequestGuests] = useState(
    selectedPeople || (type === RESERVATION_TYPES.WAITLIST ? 2 : 12)
  );
  
  const [hasAllergies, setHasAllergies] = useState(null);
  const [terms, setTerms] = useState({
    conditions: false,
    privacy: false,
    marketing: false
  });

  const isWaitlist = type === RESERVATION_TYPES.WAITLIST;
  const title = isWaitlist ? 'Lista de Espera' : 'Solicitud de Grupo';

  // Para la selección de número de comensales en grupos
  const guestOptions = isWaitlist 
    ? Array.from({ length: 10 }, (_, i) => i + 1) // 1-10 para lista de espera
    : Array.from({ length: 20 }, (_, i) => i + 10); // 10-30 para grupos

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (formData.email !== formData.emailConfirm) {
      alert('Los emails no coinciden');
      return;
    }
    
    if (formData.phone !== formData.phoneConfirm) {
      alert('Los teléfonos no coinciden');
      return;
    }

    setLoading(true);
    
    try {
      // Convertir fecha en formato dd/mm/yyyy
      const dateObj = new Date(requestDate);
      const formattedDate = dateObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      // Componer los datos de la reserva
      const reservationData = {
        date: formattedDate,
        time: requestTime, // Vacío para lista de espera, hora específica para grupos
        guests: parseInt(requestGuests),
        zone: 'Salón', // Por defecto
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          comments: formData.comments || '',
          allergies: hasAllergies ? formData.allergiesDetails : null
        }
      };
      
      await createSpecialReservation(reservationData, type);
      onSuccess();
    } catch (error) {
      console.error('Error al crear la solicitud especial:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto bg-white p-6 rounded-lg">
      <h2 className="text-xl font-bold text-center mb-6">{title}</h2>
      
      <div className="mb-6">
        <p className="text-gray-600">
          {isWaitlist 
            ? 'Complete este formulario para unirse a nuestra lista de espera. Le contactaremos en caso de que se libere una mesa en la fecha seleccionada.'
            : 'Complete este formulario para solicitar una reserva de grupo. Nuestro equipo le contactará para confirmar los detalles.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Campos específicos de reserva: fecha, comensales, hora (solo para grupos) */}
        <div className="p-4 bg-gray-50 rounded-lg mb-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            Detalles de la solicitud
          </h3>
          
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-sm mb-1">Fecha{isWaitlist ? ' preferida' : ''}</label>
              <input
                type="date"
                required
                className="w-full p-2 border rounded"
                value={requestDate}
                onChange={(e) => setRequestDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <label className="block text-sm mb-1">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  Comensales
                </div>
              </label>
              <select
                className="w-full p-2 border rounded"
                value={requestGuests}
                onChange={(e) => setRequestGuests(e.target.value)}
                required
              >
                {guestOptions.map(num => (
                  <option key={num} value={num}>{num} personas</option>
                ))}
              </select>
            </div>
          </div>
          
          {!isWaitlist && (
            <div>
              <label className="block text-sm mb-1">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  Hora preferida
                </div>
              </label>
              <select
                className="w-full p-2 border rounded"
                value={requestTime}
                onChange={(e) => setRequestTime(e.target.value)}
                required
              >
                <optgroup label="Comidas">
                  <option value="13:00">13:00</option>
                  <option value="13:15">13:15</option>
                  <option value="13:30">13:30</option>
                  <option value="13:45">13:45</option>
                  <option value="14:00">14:00</option>
                  <option value="14:15">14:15</option>
                  <option value="14:30">14:30</option>
                  <option value="14:45">14:45</option>
                  <option value="15:00">15:00</option>
                  <option value="15:15">15:15</option>
                  <option value="15:30">15:30</option>
                  <option value="15:45">15:45</option>
                  <option value="16:00">16:00</option>
                </optgroup>
                <optgroup label="Cenas">
                  <option value="20:00">20:00</option>
                  <option value="20:15">20:15</option>
                  <option value="20:30">20:30</option>
                  <option value="20:45">20:45</option>
                  <option value="21:00">21:00</option>
                  <option value="21:15">21:15</option>
                  <option value="21:30">21:30</option>
                  <option value="21:45">21:45</option>
                  <option value="22:00">22:00</option>
                  <option value="22:15">22:15</option>
                  <option value="22:30">22:30</option>
                </optgroup>
              </select>
            </div>
          )}
        </div>

        {/* Información personal */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Nombre</label>
            <input
              type="text"
              name="firstName"
              required
              className="w-full p-2 border rounded"
              value={formData.firstName}
              onChange={handleFormChange}
              placeholder="Nombre"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Apellidos</label>
            <input
              type="text"
              name="lastName"
              required
              className="w-full p-2 border rounded"
              value={formData.lastName}
              onChange={handleFormChange}
              placeholder="Apellidos"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-1">Email</label>
          <input
            type="email"
            name="email"
            required
            className="w-full p-2 border rounded"
            value={formData.email}
            onChange={handleFormChange}
            placeholder="Email"
          />
        </div>

        <div>
          <label className="block text-sm mb-1">Repetir email</label>
          <input
            type="email"
            name="emailConfirm"
            required
            className="w-full p-2 border rounded"
            value={formData.emailConfirm}
            onChange={handleFormChange}
            placeholder="Repetir email"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Prefijo</label>
            <select 
              className="w-full p-2 border rounded"
              defaultValue="+34"
            >
              <option value="+34">España (+34)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Teléfono</label>
            <input
              type="tel"
              name="phone"
              required
              className="w-full p-2 border rounded"
              value={formData.phone}
              onChange={handleFormChange}
              placeholder="Teléfono"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-1">Repetir teléfono</label>
          <input
            type="tel"
            name="phoneConfirm"
            required
            className="w-full p-2 border rounded"
            value={formData.phoneConfirm}
            onChange={handleFormChange}
            placeholder="Repetir teléfono"
          />
        </div>

        <div>
          <label className="block text-sm mb-1">
            {isWaitlist
              ? 'Indique sus preferencias o requisitos especiales'
              : 'Indique detalles sobre su grupo, preferencias o requisitos especiales'}
          </label>
          <textarea
            name="comments"
            className="w-full p-2 border rounded"
            rows={3}
            value={formData.comments}
            onChange={handleFormChange}
            placeholder={isWaitlist
              ? 'Ej: Preferencia de horario, zona...'
              : 'Ej: Tipo de evento, menú especial...'}
          />
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm mb-2">
              ¿Tiene algún comensal alguna intolerancia/alergia?
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="allergies"
                  value="yes"
                  onChange={() => setHasAllergies(true)}
                  className="mr-2"
                />
                Sí
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="allergies"
                  value="no"
                  onChange={() => setHasAllergies(false)}
                  className="mr-2"
                />
                No
              </label>
            </div>
          </div>

          {hasAllergies && (
            <div className="mt-2">
              <label className="block text-sm mb-1">
                Por favor, indique las alergias o intolerancias:
              </label>
              <textarea
                name="allergiesDetails"
                className="w-full p-2 border rounded"
                rows={3}
                value={formData.allergiesDetails}
                onChange={handleFormChange}
                placeholder="Describa aquí las alergias o intolerancias"
              />
            </div>
          )}
        </div>

        <div className="space-y-4 mt-8">
          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                required
                className="mt-1 mr-2"
                checked={terms.conditions}
                onChange={(e) => setTerms({...terms, conditions: e.target.checked})}
              />
              <span className="text-sm">
                Acepto las <LegalTermsLink />
              </span>
            </label>
          </div>
          
          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                required
                className="mt-1 mr-2"
                checked={terms.privacy}
                onChange={(e) => setTerms({...terms, privacy: e.target.checked})}
              />
              <span className="text-sm">
                Consiento el <DataProtectionLink />
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                className="mt-1 mr-2"
                checked={terms.marketing}
                onChange={(e) => setTerms({...terms, marketing: e.target.checked})}
              />
              <span className="text-sm">
                Consiento la recepción de comunicaciones del restaurante por e-mail y/o SMS con fines comerciales
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="py-3 px-6 border border-[#e31837] text-[#e31837] rounded hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="py-3 px-6 bg-[#e31837] text-white rounded hover:bg-[#c41230] disabled:opacity-50"
          >
            {loading ? 'Enviando...' : 'Enviar Solicitud'}
          </button>
        </div>
      </form>

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialRequestForm;